import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Form,
  Input,
  Switch,
  Typography,
  message,
  Alert,
  Space,
  Button,
  Empty,
  Divider,
} from 'antd';
import {
  TeamOutlined,
  RobotOutlined,
  SearchOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type { AgentDefinition, AiAgentConsultingPeer } from '@/api/types';
import type { AgentSearchParams } from '@/api/agents';
import { useAgentDirectory, useAgentRow } from '@/components/useAgentDirectory';
import { AgentStatusPill, AgentStatusToggle, statusParam, type AgentStatusFilter } from '@/components/agentStatus';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import {
  PeerCandidateFilterDrawer,
  DEFAULT_FILTERS,
  activeFilterCount,
  type PeerCandidateFilters,
} from './PeerCandidateFilterDrawer';

const { Text, Paragraph } = Typography;

interface PeerEditorDrawerProps {
  open: boolean;
  // null = create mode, otherwise edit the existing row
  peer: AiAgentConsultingPeer | null;
  onClose: () => void;
}

// Drawer for picking + configuring an A2A peer link. Two-step flow:
//   1. Search and click a candidate agent — rich card with audience pills
//   2. Fill in scope + exclusion + active toggle in the form section below
//
// In edit mode the candidate list collapses to the linked agent and
// peerAgentId becomes immutable to avoid silently rewiring an existing link.
//
// The candidates come from the SERVER (2 Nov 2026): it searches, filters and leaves out this agent and its peers, a
// page at a time. The drawer used to filter the first 100 of the practice's agents in the browser.

/** Candidates per request; "Show more" asks for the next page. */
const PEER_PAGE_SIZE = 50;

export const PeerEditorDrawer = observer(function PeerEditorDrawer({
  open,
  peer,
  onClose,
}: PeerEditorDrawerProps) {
  const { agentDetailStore: detail, merchantContextStore, themeStore } = useStore();
  const merchantId = merchantContextStore.selectedMerchantId;
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<{
    scope: string;
    exclusionGuidance: string;
    isActive: boolean;
  }>();
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<PeerCandidateFilters>(DEFAULT_FILTERS);
  const [status, setStatus] = useState<AgentStatusFilter>('all');

  const isEdit = peer != null;
  const currentAgentId = detail.agentId;
  const filterCount = activeFilterCount(filters);

  // Reset state when the drawer opens or the peer being edited changes.
  useEffect(() => {
    if (open) {
      setSearch('');
      setFilters(DEFAULT_FILTERS);
      setStatus('all');
      setFilterOpen(false);
      setSelectedAgentId(peer?.peerAgentId ?? null);
      form.setFieldsValue({
        scope: peer?.scope ?? '',
        exclusionGuidance: peer?.exclusionGuidance ?? '',
        isActive: peer?.isActive ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, peer?.id]);

  // Candidates (adding a peer; editing shows the linked one): the practice's agents that aren't this agent and aren't
  // already a peer, with the user's filters, by name — all decided by the server.
  const request = useMemo<AgentSearchParams | null>(() => {
    if (!open || isEdit || merchantId == null) return null;
    const linkedIds = detail.peers.map((p) => p.peerAgentId);
    return {
      merchantId,
      search: search.trim() || undefined,
      audience: filters.audience === 'any' ? undefined : filters.audience === 'careTeam' ? 'care' : filters.audience,
      status: statusParam(status),
      router: filters.router === 'any' ? undefined : filters.router,
      consults: filters.consults === 'any' ? undefined : filters.consults,
      excludeIds: currentAgentId != null ? [currentAgentId, ...linkedIds] : linkedIds,
      sort: 'name',
      pageSize: PEER_PAGE_SIZE,
    };
  }, [open, isEdit, merchantId, detail.peers, currentAgentId, search, filters, status]);
  const directory = useAgentDirectory(request);
  const candidates = directory.rows;

  // Edit mode: collapse to a single row showing the already-linked peer (its directory row, else its stored name).
  const linkedRow = useAgentRow(isEdit ? peer!.peerAgentId : null);
  const visibleCandidates = useMemo(() => {
    if (!isEdit) return candidates;
    if (linkedRow) return [linkedRow];
    return [
      {
        id: peer!.peerAgentId,
        displayName: peer!.peerAgentName,
        a2AAgentName: undefined,
      } as AgentDefinition,
    ];
  }, [isEdit, candidates, linkedRow, peer]);

  async function handleSubmit(): Promise<void> {
    if (selectedAgentId == null) {
      message.warning('Pick a peer agent first.');
      return;
    }
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (isEdit) {
        await detail.updatePeer({
          id: peer!.id,
          peerAgentId: selectedAgentId,
          scope: values.scope?.trim(),
          exclusionGuidance: values.exclusionGuidance?.trim(),
          isActive: values.isActive,
        });
        message.success('Peer updated');
        trackEvent('Peer.Updated', { peerId: peer!.id });
      } else {
        await detail.addPeer({
          peerAgentId: selectedAgentId,
          scope: values.scope?.trim(),
          exclusionGuidance: values.exclusionGuidance?.trim(),
          isActive: values.isActive,
        });
        message.success('Peer added');
        trackEvent('Peer.Added', { peerAgentId: selectedAgentId });
      }
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to save peer');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Nothing to offer with no search and no filters: every other agent of the practice is already a peer.
  const noCandidates = !isEdit && directory.total === 0 && !search.trim() && filterCount === 0 && status === 'all';

  return (
    <>
    <Drawer
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <TeamOutlined /> {isEdit ? 'Edit peer agent' : 'Add peer agent'}
        </span>
      }
      width={620}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedAgentId == null
              ? 'Step 1 of 2 — pick an agent above'
              : isEdit
              ? 'Update scope, exclusion, or status'
              : 'Step 2 of 2 — set scope & exclusion'}
          </Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={selectedAgentId == null}
              onClick={handleSubmit}
              icon={<CheckOutlined />}
            >
              {isEdit ? 'Save changes' : 'Add peer'}
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ padding: '18px 20px' }}>
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 14 }}>
          When this agent is talking to a user, it can route a sub-question to a peer that
          specializes in a different domain. Pick a peer below, then describe <i>when</i> to
          consult and <i>when not to</i>.
        </Paragraph>

        {/* ── Step 1: pick the peer ─────────────────────────────────────── */}
        <SectionLabel isDark={isDark}>
          1 · {isEdit ? 'Linked agent' : 'Pick a peer agent'}
        </SectionLabel>

        {!isEdit && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Input
              allowClear
              placeholder="Search by name, slug, or description…"
              prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterOpen(true)}
              type={filterCount > 0 ? 'primary' : 'default'}
              ghost={filterCount > 0}
            >
              Filter
              {filterCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    background: colors.ink[600],
                    color: '#FFFFFF',
                    padding: '0 6px',
                    borderRadius: 999,
                    lineHeight: '16px',
                    display: 'inline-block',
                    minWidth: 16,
                    textAlign: 'center',
                  }}
                >
                  {filterCount}
                </span>
              )}
            </Button>
          </div>
        )}

        {!isEdit && (
          <div style={{ marginBottom: 10 }}>
            <AgentStatusToggle value={status} onChange={setStatus} />
          </div>
        )}

        {/* Result count + clear-filters when filters are active */}
        {!isEdit && filterCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              background: isDark ? colors.slate[700] : colors.ink[50],
              border: `1px solid ${isDark ? colors.slate[600] : colors.ink[100]}`,
              borderRadius: 8,
              marginBottom: 10,
            }}
          >
            <Text style={{ fontSize: 12, color: isDark ? colors.ink[100] : colors.ink[700] }}>
              <b>{directory.total ?? '…'}</b> {directory.total === 1 ? 'agent matches' : 'agents match'} {filterCount}{' '}
              {filterCount === 1 ? 'filter' : 'filters'}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
            >
              Clear
            </Button>
          </div>
        )}

        {!isEdit && directory.error ? (
          <Alert type="error" showIcon message="Failed to load agents" description={directory.error} />
        ) : !isEdit && directory.loading && candidates.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Loading agents…
          </Text>
        ) : noCandidates ? (
          <Alert
            type="info"
            showIcon
            message="All eligible agents are already peers"
            description="Every other agent in this practice is already linked. Remove a peer first if you want to swap one in."
          />
        ) : visibleCandidates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                {search.trim()
                  ? `No agent matches “${search.trim()}”${filterCount > 0 ? ' with these filters' : ''}.`
                  : 'No agents match these filters.'}
              </span>
            }
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 280,
              overflow: 'auto',
              paddingRight: 4,
            }}
          >
            {visibleCandidates.map((a) => (
              <CandidateCard
                key={a.id}
                agent={a}
                selected={selectedAgentId === a.id}
                onSelect={() => setSelectedAgentId(a.id)}
                isDark={isDark}
                disabled={isEdit}
              />
            ))}
            {!isEdit && directory.more && !directory.loading && (
              <div style={{ textAlign: 'center', padding: '4px 0' }}>
                <Button size="small" loading={directory.loadingMore} onClick={directory.showMore}>
                  Show more ({directory.total! - candidates.length} more)
                </Button>
              </div>
            )}
          </div>
        )}

        <Divider style={{ margin: '20px 0' }} />

        {/* ── Step 2: scope & exclusion ─────────────────────────────────── */}
        <SectionLabel isDark={isDark}>
          2 · Consultation scope
          {selectedAgentId == null && (
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              <ArrowRightOutlined style={{ fontSize: 10 }} /> select an agent above to enable
            </Text>
          )}
        </SectionLabel>

        <Form
          form={form}
          layout="vertical"
          preserve={false}
          requiredMark="optional"
          disabled={selectedAgentId == null}
        >
          <Form.Item
            name="scope"
            label="When to consult"
            extra='Plain English. e.g. "Consult for ANY question about meal plans, portion sizes, or nutritional content."'
            rules={[{ max: 1000 }]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item
            name="exclusionGuidance"
            label="When NOT to consult"
            extra='Use to prevent overlap with other peers. e.g. "Do NOT consult for medication dosing — those go to the Medication Agent."'
            rules={[{ max: 1000 }]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item label="Status" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </div>
    </Drawer>

    {/* Nested filter drawer — slides in over the main drawer when active. */}
    <PeerCandidateFilterDrawer
      open={filterOpen}
      filters={filters}
      onChange={setFilters}
      onClose={() => setFilterOpen(false)}
    />
    </>
  );
});

// ── Candidate card ─────────────────────────────────────────────────────────

const CandidateCard = observer(function CandidateCard({
  agent,
  selected,
  onSelect,
  isDark,
  disabled,
}: {
  agent: AgentDefinition;
  selected: boolean;
  onSelect: () => void;
  isDark: boolean;
  disabled?: boolean;
}) {
  // Quieter visual hierarchy than the previous version: name dominates;
  // audience is a single small pill on the right; capabilities are reduced to
  // monochrome inline dots so the eye scans 5+ rows without fatigue.
  const audienceLabel =
    agent.patientApp && agent.merchantApp
      ? 'Both'
      : agent.patientApp
      ? 'Patient'
      : agent.merchantApp
      ? 'Care team'
      : null;

  const audienceTone =
    audienceLabel === 'Both'
      ? colors.violet[600]
      : audienceLabel === 'Patient'
      ? colors.ink[600]
      : audienceLabel === 'Care team'
      ? colors.violet[600]
      : colors.slate[400];

  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        textAlign: 'left',
        background: selected
          ? isDark
            ? `${colors.ink[700]}40`
            : colors.ink[50]
          : isDark
          ? colors.slate[800]
          : '#FFFFFF',
        border: `1px solid ${
          selected ? colors.ink[600] : isDark ? colors.slate[700] : colors.slate[200]
        }`,
        borderRadius: 10,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 120ms ease, background 120ms ease',
        opacity: disabled ? 0.85 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !selected) e.currentTarget.style.borderColor = colors.ink[500];
      }}
      onMouseLeave={(e) => {
        if (!disabled && !selected) {
          e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
        }
      }}
    >
      {/* Avatar — selected state swaps to a check */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: selected
            ? colors.ink[600]
            : isDark
            ? `${colors.ink[700]}55`
            : colors.ink[50],
          color: selected ? '#FFFFFF' : colors.ink[600],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
          transition: 'background 120ms ease',
        }}
      >
        {selected ? <CheckOutlined /> : <RobotOutlined />}
      </div>

      {/* Name + slug */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: isDark ? colors.slate[50] : colors.slate[900],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.005em',
          }}
        >
          {agent.displayName ?? `Agent #${agent.id}`}
        </div>
        {agent.a2AAgentName && (
          <div
            style={{
              fontSize: 11,
              fontFamily: "'JetBrains Mono', monospace",
              color: isDark ? colors.slate[500] : colors.slate[400],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: 1,
            }}
          >
            {agent.a2AAgentName}
          </div>
        )}
      </div>

      <AgentStatusPill agent={agent} />

      {/* Capability dots — monochrome, on hover the tooltip names them. */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          flexShrink: 0,
        }}
        aria-hidden
      >
        <CapabilityDot
          on={!!agent.enableSkillRouter}
          isDark={isDark}
          title={agent.enableSkillRouter ? 'Skill router on' : 'Skill router off'}
        />
        <CapabilityDot
          on={!!agent.enableConsultation}
          isDark={isDark}
          title={agent.enableConsultation ? 'Consults peers' : 'No peer consultation'}
        />
      </div>

      {/* Audience pill — single, quiet */}
      {audienceLabel && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 9px',
            borderRadius: 999,
            border: `1px solid ${audienceTone}33`,
            color: audienceTone,
            background: 'transparent',
            flexShrink: 0,
            letterSpacing: '0.01em',
          }}
        >
          {audienceLabel}
        </span>
      )}
    </button>
  );
});

// Two-state monochrome dot: filled when capability is on, ringed when off.
function CapabilityDot({
  on,
  isDark,
  title,
}: {
  on: boolean;
  isDark: boolean;
  title: string;
}) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: 999,
        background: on ? colors.green[600] : 'transparent',
        border: `1px solid ${on ? colors.green[600] : isDark ? colors.slate[600] : colors.slate[300]}`,
      }}
    />
  );
}

// ── Section label ─────────────────────────────────────────────────────────

function SectionLabel({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: isDark ? colors.slate[400] : colors.slate[500],
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}
